import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { formatInr } from "../lib/accountsSettlement";
import { PageHeader, Pill, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PartyLedgerExportDialog from "../components/PartyLedgerExportDialog";
import TallySyncMappingDialog from "../components/TallySyncMappingDialog";
import { VoucherBriefDialog } from "../components/purchase/FinancialAdjustmentBriefDialogs";
import PartyTdsAnalysisPanel from "../components/PartyTdsAnalysisPanel";
import { useAuth, hasPermission } from "../context/AuthContext";
import { toast } from "sonner";
import { CloudUpload, Loader2, RefreshCw, Search } from "lucide-react";

function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function fyStartIso(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return `${startYear}-04-01`;
}

function isoDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthStartIso(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function quarterStartIso(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3) * 3;
  const prevQ = q - 3;
  const year = prevQ < 0 ? d.getFullYear() - 1 : d.getFullYear();
  const month = ((prevQ + 12) % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

const TYPE_TONE = {
  MATERIAL: "info",
  PURCHASE: "info",
  FREIGHT: "warning",
  TDS: "default",
  CASH: "warning",
  PAYMENT: "warning",
  RECEIPT: "success",
  SALES: "info",
  JOURNAL: "default",
};

/**
 * Universal General Ledger & Statement view.
 * @param {{ lockedLedgerId?: string|null }} props
 */
export default function GeneralLedgerView({ lockedLedgerId = null } = {}) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const initialLedger =
    lockedLedgerId
    || (params.id ? `party:${params.id}` : "")
    || searchParams.get("ledger")
    || (searchParams.get("party_id") ? `party:${searchParams.get("party_id")}` : "")
    || "";

  const [categories, setCategories] = useState([]);
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [ledgerId, setLedgerId] = useState(String(initialLedger || ""));
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || fyStartIso());
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || isoDate());
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [drawerTx, setDrawerTx] = useState(null);
  const [ledgerTab, setLedgerTab] = useState(() => (searchParams.get("tab") === "tds" ? "tds" : "statement"));
  const [tallySyncTargets, setTallySyncTargets] = useState(null);
  const [tallySyncLoading, setTallySyncLoading] = useState(false);
  const canTallySync =
    hasPermission(user, "tally.sync")
    || hasPermission(user, "vouchers.edit")
    || hasPermission(user, "page.accounts");

  useEffect(() => {
    if (lockedLedgerId) setLedgerId(String(lockedLedgerId));
    else if (params.id) setLedgerId(`party:${params.id}`);
  }, [lockedLedgerId, params.id]);

  useEffect(() => {
    (async () => {
      setDropdownLoading(true);
      try {
        const { data } = await api.get("/accounts/ledgers/dropdown");
        setCategories(data?.categories || []);
      } catch (err) {
        toast.error(extractError(err));
      } finally {
        setDropdownLoading(false);
      }
    })();
  }, []);

  const flatLedgers = useMemo(
    () => (categories || []).flatMap((c) => (c.items || []).map((it) => ({ ...it, categoryLabel: c.label }))),
    [categories]
  );

  const filteredLedgers = useMemo(() => {
    const q = ledgerQuery.trim().toLowerCase();
    if (!q) return flatLedgers.slice(0, 250);
    return flatLedgers
      .filter((l) => {
        const hay = [l.name, l.subtitle, l.code, l.key, l.gstin, l.categoryLabel]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 250);
  }, [flatLedgers, ledgerQuery]);

  const selectedLedger = useMemo(
    () =>
      flatLedgers.find((l) => String(l.ledger_id) === String(ledgerId))
      || statement?.ledger_info
      || null,
    [flatLedgers, ledgerId, statement]
  );

  const loadStatement = useCallback(async () => {
    if (!ledgerId) {
      setStatement(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/accounts/ledgers/${ledgerId}/statement`, {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          include_drafts: includeDrafts,
        },
      });
      setStatement(data);
      if (!lockedLedgerId && !params.id) {
        const next = new URLSearchParams();
        next.set("ledger", String(ledgerId));
        if (startDate) next.set("start_date", startDate);
        if (endDate) next.set("end_date", endDate);
        setSearchParams(next, { replace: true });
      }
    } catch (err) {
      toast.error(extractError(err));
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [ledgerId, startDate, endDate, includeDrafts, lockedLedgerId, params.id, setSearchParams]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const applyPreset = (key) => {
    const today = new Date();
    if (key === "month") {
      setStartDate(monthStartIso(today));
      setEndDate(isoDate(today));
    } else if (key === "quarter") {
      setStartDate(quarterStartIso(today));
      setEndDate(isoDate(today));
    } else if (key === "fy") {
      setStartDate(fyStartIso(today));
      setEndDate(isoDate(today));
    }
  };

  const txs = statement?.transactions || [];
  const typeSummary = statement?.type_summary || [];
  const ledgerName =
    statement?.ledger_info?.name
    || selectedLedger?.name
    || statement?.party?.display_name
    || "";

  const locked = Boolean(lockedLedgerId || params.id);
  const partyIdForTds =
    statement?.ledger_info?.party_id
    || (ledgerId.startsWith("party:") ? ledgerId.split(":")[1] : null);

  const openPartyLedgerTallySync = async () => {
    if (!partyIdForTds) return;
    setTallySyncLoading(true);
    try {
      const { data } = await api.get(`/accounts/parties/${partyIdForTds}/ledger/tally-sync-targets`, {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          include_drafts: includeDrafts,
        },
      });
      const vouchers = data?.vouchers || [];
      if (!vouchers.length) {
        toast.message("No Tally-syncable vouchers in this ledger period");
        return;
      }
      setTallySyncTargets(vouchers);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setTallySyncLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="general-ledger-page">
      {!locked ? (
        <PageHeader
          title="General Ledger & Statement"
          description="Tally-style running balance for any ledger — parties, purchases, freight, TDS, bank, cash, and expenses."
          actions={
            <Button variant="outline" size="sm" onClick={loadStatement} disabled={loading || !ledgerId}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          }
        />
      ) : (
        <PageHeader
          title={ledgerName || "Party ledger"}
          description="Running balance statement for this ledger."
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to="/accounts/ledger">All ledgers</Link>
            </Button>
          }
        />
      )}

      <SectionCard title="Filters" subtitle="Select ledger and period">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="xl:col-span-2 space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Ledger</label>
            {!locked ? (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    className="pl-8 h-9"
                    placeholder="Search parties, cash, bank, TDS, expenses…"
                    value={ledgerQuery}
                    onChange={(e) => setLedgerQuery(e.target.value)}
                  />
                </div>
                <select
                  className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                  value={ledgerId}
                  onChange={(e) => setLedgerId(e.target.value)}
                  disabled={dropdownLoading}
                  data-testid="gl-ledger-select"
                >
                  <option value="">{dropdownLoading ? "Loading ledgers…" : "Select ledger…"}</option>
                  {(categories || []).map((cat) => (
                    <optgroup key={cat.id} label={cat.label}>
                      {(cat.items || [])
                        .filter((it) => {
                          if (!ledgerQuery.trim()) return true;
                          const q = ledgerQuery.trim().toLowerCase();
                          const hay = [it.name, it.subtitle, it.code, it.key, it.gstin]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase();
                          return hay.includes(q);
                        })
                        .slice(0, 200)
                        .map((it) => (
                          <option key={it.ledger_id} value={it.ledger_id}>
                            {it.name}
                            {it.code && it.code !== it.name ? ` · ${it.code}` : ""}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                  {/* Keep current selection visible even if filtered out */}
                  {ledgerId && !filteredLedgers.some((l) => l.ledger_id === ledgerId) && selectedLedger ? (
                    <option value={ledgerId}>{selectedLedger.name}</option>
                  ) : null}
                </select>
              </>
            ) : (
              <div className="h-10 flex items-center rounded-md border border-border bg-muted/40 px-3 text-sm font-medium">
                {ledgerName || ledgerId}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">From</label>
            <Input
              type="date"
              className="mt-1 h-10"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">To</label>
            <Input
              type="date"
              className="mt-1 h-10"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("month")}>
            This month
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("quarter")}>
            Last quarter
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyPreset("fy")}>
            FY YTD
          </Button>
          <label className="ml-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeDrafts}
              onChange={(e) => setIncludeDrafts(e.target.checked)}
            />
            Include drafts
          </label>
          <div className="flex-1" />
          {partyIdForTds && canTallySync ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={loading || tallySyncLoading || !ledgerId}
              onClick={openPartyLedgerTallySync}
              data-testid="party-ledger-tally-sync"
            >
              {tallySyncLoading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4 mr-1" />
              )}
              Sync ledger to Tally
            </Button>
          ) : null}
          <PartyLedgerExportDialog
            ledgerId={ledgerId}
            partyId={ledgerId.startsWith("party:") ? ledgerId.split(":")[1] : ""}
            partyName={ledgerName}
            startDate={startDate}
            endDate={endDate}
            includeDrafts={includeDrafts}
            disabled={loading}
          />
        </div>
      </SectionCard>

      {!ledgerId ? (
        <p className="text-sm text-muted-foreground text-center py-12">
          Select a ledger to load the statement.
        </p>
      ) : loading && !statement ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading statement…
        </div>
      ) : statement ? (
        <>
          {partyIdForTds ? (
            <Tabs value={ledgerTab} onValueChange={setLedgerTab} className="space-y-4">
              <TabsList className="bg-card border border-border p-1 h-auto">
                <TabsTrigger value="statement">Ledger statement</TabsTrigger>
                <TabsTrigger value="tds">TDS analysis</TabsTrigger>
              </TabsList>
              <TabsContent value="statement" className="space-y-4 mt-0">
                <LedgerStatementBody
                  statement={statement}
                  txs={txs}
                  typeSummary={typeSummary}
                  ledgerName={ledgerName}
                  ledgerId={ledgerId}
                  startDate={startDate}
                  endDate={endDate}
                  includeDrafts={includeDrafts}
                  loading={loading}
                  onSelectTx={setDrawerTx}
                />
              </TabsContent>
              <TabsContent value="tds" className="mt-0">
                <PartyTdsAnalysisPanel
                  partyId={partyIdForTds}
                  showLedgerLink
                  onViewLedger={() => setLedgerTab("statement")}
                  onPostedLedgers={() => {
                    setLedgerTab("statement");
                    loadStatement();
                  }}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <LedgerStatementBody
              statement={statement}
              txs={txs}
              typeSummary={typeSummary}
              ledgerName={ledgerName}
              ledgerId={ledgerId}
              startDate={startDate}
              endDate={endDate}
              includeDrafts={includeDrafts}
              loading={loading}
              onSelectTx={setDrawerTx}
            />
          )}
        </>
      ) : null}

      <VoucherBriefDialog
        open={Boolean(drawerTx)}
        onOpenChange={(open) => !open && setDrawerTx(null)}
        voucherId={drawerTx?.voucher_id}
        voucherCode={drawerTx?.voucher_code}
        label={drawerTx?.voucher_type}
        ledgerContext={drawerTx}
        presentation="sheet"
        partyId={statement?.ledger_info?.party_id}
      />

      <TallySyncMappingDialog
        open={Boolean(tallySyncTargets?.length)}
        onOpenChange={(open) => {
          if (!open) setTallySyncTargets(null);
        }}
        voucherIds={tallySyncTargets || []}
        onSynced={() => {
          setTallySyncTargets(null);
          loadStatement();
        }}
      />
    </div>
  );
}

function LedgerStatementBody({
  statement,
  txs,
  typeSummary,
  ledgerName,
  ledgerId,
  startDate,
  endDate,
  includeDrafts,
  loading,
  onSelectTx,
}) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Opening balance" value={statement.opening_balance_display} />
        <Kpi label="Total debits (Dr)" value={formatInr(statement.total_debit)} tone="amber" />
        <Kpi label="Total credits (Cr)" value={formatInr(statement.total_credit)} tone="blue" />
        <Kpi label="Closing balance" value={statement.closing_balance_display} emphasize />
      </div>

      {typeSummary.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="gl-type-summary">
          {typeSummary.map((row) => (
            <div
              key={row.voucher_type}
              className="rounded-md border border-border bg-card px-3 py-2 text-xs min-w-[9rem]"
            >
              <div className="font-semibold tracking-wide">{row.voucher_type}</div>
              <div className="text-muted-foreground mt-0.5">{row.count} entries</div>
              <div className="tabular-nums mt-1">
                Dr {formatInr(row.total_debit)} · Cr {formatInr(row.total_credit)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <SectionCard
        title={`${ledgerName || "Ledger"} · ${txs.length} entries`}
        subtitle={
          statement.ledger_info?.code
            ? `${statement.ledger_info.code}${statement.ledger_info.gstin ? ` · GSTIN ${statement.ledger_info.gstin}` : ""}`
            : statement.ledger_info?.account_type || statement.party?.gstin || "Statement"
        }
        action={
          <PartyLedgerExportDialog
            ledgerId={ledgerId}
            partyId={ledgerId.startsWith("party:") ? ledgerId.split(":")[1] : ""}
            partyName={ledgerName}
            startDate={startDate}
            endDate={endDate}
            includeDrafts={includeDrafts}
            disabled={loading}
            testId="gl-export-table"
          />
        }
      >
        <div className="hidden md:block overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm min-w-[920px]">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2">Date</th>
                <th className="text-left font-medium px-3 py-2">Particulars / Opposite</th>
                <th className="text-left font-medium px-3 py-2">Type</th>
                <th className="text-left font-medium px-3 py-2">Voucher No.</th>
                <th className="text-right font-medium px-3 py-2">Debit (₹)</th>
                <th className="text-right font-medium px-3 py-2">Credit (₹)</th>
                <th className="text-right font-medium px-3 py-2">Running Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border bg-muted/30">
                <td className="px-3 py-2 text-xs">{startDate || "—"}</td>
                <td className="px-3 py-2 font-medium" colSpan={3}>
                  Opening Balance
                </td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums font-semibold">
                  {statement.opening_balance_display}
                </td>
              </tr>
              {txs.map((tx, idx) => (
                <tr
                  key={`${tx.voucher_id || tx.entry_id || idx}-${tx.voucher_type}-${tx.debit}-${tx.credit}`}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => onSelectTx(tx)}
                >
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{tx.date || "—"}</td>
                  <td className="px-3 py-2 max-w-[300px]">
                    <div className="truncate">{tx.particulars || "—"}</div>
                    {tx.opposite_ledger ? (
                      <div className="text-[11px] text-muted-foreground truncate">
                        vs {tx.opposite_ledger}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <Pill tone={TYPE_TONE[tx.voucher_type] || "default"}>{tx.voucher_type}</Pill>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{tx.voucher_code}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(tx.debit) > 0 ? formatInr(tx.debit) : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(tx.credit) > 0 ? formatInr(tx.credit) : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {tx.running_balance_display}
                  </td>
                </tr>
              ))}
              {txs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    No transactions in this period.
                  </td>
                </tr>
              ) : null}
              <tr className="border-t border-border bg-muted/40 font-semibold">
                <td className="px-3 py-2" colSpan={4}>
                  Totals / Closing
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{formatInr(statement.total_debit)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatInr(statement.total_credit)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{statement.closing_balance_display}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-2">
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <div className="text-xs text-muted-foreground">Opening</div>
            <div className="font-semibold">{statement.opening_balance_display}</div>
          </div>
          {txs.map((tx, idx) => (
            <button
              type="button"
              key={`${tx.voucher_id || tx.entry_id || idx}-m`}
              onClick={() => onSelectTx(tx)}
              className="w-full text-left rounded-md border border-border bg-card px-3 py-2.5 space-y-1"
            >
              <div className="flex justify-between gap-2">
                <span className="font-mono text-xs">{tx.voucher_code}</span>
                <Pill tone={TYPE_TONE[tx.voucher_type] || "default"}>{tx.voucher_type}</Pill>
              </div>
              <div className="text-xs text-muted-foreground">{tx.date}</div>
              <div className="text-sm truncate">{tx.particulars}</div>
              <div className="flex justify-between text-xs tabular-nums pt-1">
                <span>Dr {money(tx.debit) > 0 ? formatInr(tx.debit) : "—"}</span>
                <span>Cr {money(tx.credit) > 0 ? formatInr(tx.credit) : "—"}</span>
                <span className="font-semibold">{tx.running_balance_display}</span>
              </div>
            </button>
          ))}
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm font-semibold flex justify-between">
            <span>Closing</span>
            <span>{statement.closing_balance_display}</span>
          </div>
        </div>
      </SectionCard>
    </>
  );
}

function Kpi({ label, value, tone, emphasize }) {
  const tones = {
    amber: "text-amber-800 dark:text-amber-200",
    blue: "text-sky-800 dark:text-sky-200",
  };
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p
        className={`text-lg font-semibold tabular-nums mt-1 ${
          emphasize ? "text-foreground" : tones[tone] || "text-foreground"
        }`}
      >
        {value}
      </p>
    </div>
  );
}